import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tabs,
  Tab,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { AppProvider } from "../../ContextApi/AppProvider";
import type { FormaPagamento } from "../Shared/Interfaces/FormaPagamento";
import type { Parcela } from "../Shared/Interfaces/Parcela";
import { FormaPagamentoService } from "../../Services/FormaPagamentoService";
import { ParcelaService } from "../../Services/ParcelaService";
import DefaultLoader from "../Shared/DefaultLoader";
import colors from "../../theme/colors";

const formatTaxa = (value: string) => {
  const numericValue = value.replace(/\D/g, '');
  
  if (!numericValue) return '0,00';
  
  const decimalValue = (parseInt(numericValue) / 100).toFixed(2);
  
  return decimalValue.replace('.', ',');
};

export default function FormaPagamento() {
  const contextApi = useContext(AppProvider);
  const formaPagamentoService = new FormaPagamentoService();
  const parcelaService = new ParcelaService();
  const [parcelas, setParcelas] = useState<Parcela[]>([]);
  const [formasPagamento, setFormasPagamento] = useState<FormaPagamento[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingParcelas, setLoadingParcelas] = useState(false);
  const [editingParcela, setEditingParcela] = useState<Parcela | null>(null);
  const [editingFormaPagamento, setEditingFormaPagamento] = useState<FormaPagamento | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState<Parcela>({
    label: "",
    quantidadeParcelas: 0,
    taxa: 0,
  });
  const [formaPagamentoData, setFormaPagamentoData] = useState<FormaPagamento>({
    descricao: "",
    ativo: true,
    taxa: 0,
  });

  useEffect(() => {
    getAllFormasPagamento();
    getAllParcelas();
  }, []);

  const getAllFormasPagamento = async () => {
    setLoading(true);
    try {
      const response = await formaPagamentoService.getAll();
      setFormasPagamento(response.data);
    } catch (error) {
      console.error("Erro ao buscar formas de pagamento:", error);
      contextApi.showMessage("Erro ao buscar formas de pagamento", true, "error");
    } finally {
      setLoading(false);
    }
  };

  const getAllParcelas = async () => {
    setLoadingParcelas(true);
    try {
      const response = await parcelaService.getAll();
      setParcelas(response.data);
    } catch (error) {
      console.error("Erro ao buscar parcelas:", error);
      contextApi.showMessage("Erro ao buscar parcelas", true, "error");
    } finally {
      setLoadingParcelas(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleInputChange = (field: keyof Parcela, value: string | number) => {
    if (field === 'taxa') {
      const formattedValue = formatTaxa(value.toString());
      setFormData((prev) => ({
        ...prev,
        [field]: parseFloat(formattedValue.replace(',', '.')),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleFormaPagamentoInputChange = (field: keyof FormaPagamento, value: string | boolean | number) => {
    if (field === 'taxa') {
      const formattedValue = formatTaxa(value.toString());
      setFormaPagamentoData((prev) => ({
        ...prev,
        [field]: parseFloat(formattedValue.replace(',', '.')),
      }));
    } else {
      setFormaPagamentoData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingParcelas(true);
    
    try {
      if (editingParcela) {
        const response = await parcelaService.putItem({
          ...formData,
          id: editingParcela.id,
        });
        if (response.status === 204) {
          contextApi.showMessage("Parcela atualizada com sucesso!", true, "success");
          getAllParcelas();
        }
      } else {
        const response = await parcelaService.postItem(formData);
        if (response.status === 201) {
          contextApi.showMessage("Parcela adicionada com sucesso!", true, "success");
          getAllParcelas();
        }
      }

      setFormData({
        label: "",
        quantidadeParcelas: 0,
        taxa: 0,
      });
      setEditingParcela(null);
    } catch (error) {
      console.error("Erro ao salvar parcela:", error);
      contextApi.showMessage("Erro ao salvar parcela", true, "error");
    } finally {
      setLoadingParcelas(false);
    }
  };

  const handleFormaPagamentoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editingFormaPagamento) {
        const response = await formaPagamentoService.putItem({
          ...formaPagamentoData,
          id: editingFormaPagamento.id,
        });
        if (response.status === 204) {
          contextApi.showMessage("Forma de pagamento atualizada com sucesso!", true, "success");
          getAllFormasPagamento();
        }
      } else {
        const response = await formaPagamentoService.postItem(formaPagamentoData);
        if (response.status === 201) {
          contextApi.showMessage("Forma de pagamento adicionada com sucesso!", true, "success");
          getAllFormasPagamento();
        }
      }

      setFormaPagamentoData({
        descricao: "",
        ativo: true,
        taxa: 0,
      });
      setEditingFormaPagamento(null);
    } catch (error) {
      console.error("Erro ao salvar forma de pagamento:", error);
      contextApi.showMessage("Erro ao salvar forma de pagamento", true, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (parcela: Parcela) => {
    setEditingParcela(parcela);
    setFormData(parcela);
  };

  const handleEditFormaPagamento = (formaPagamento: FormaPagamento) => {
    setEditingFormaPagamento(formaPagamento);
    setFormaPagamentoData(formaPagamento);
  };

  const handleDelete = async (id: number) => {
    setLoadingParcelas(true);
    try {
      const response = await parcelaService.postDeleteItem(id);
      if (response.status === 204) {
        contextApi.showMessage("Parcela removida com sucesso!", true, "success");
        getAllParcelas();
      }
    } catch (error) {
      console.error("Erro ao remover parcela:", error);
      contextApi.showMessage("Erro ao remover parcela", true, "error");
    } finally {
      setLoadingParcelas(false);
    }
  };

  const handleDeleteFormaPagamento = async (id: number) => {
    setLoading(true);
    try {
      const response = await formaPagamentoService.postDeleteItem(id);
      if (response.status === 204) {
        contextApi.showMessage("Forma de pagamento removida com sucesso!", true, "success");
        getAllFormasPagamento();
      }
    } catch (error) {
      console.error("Erro ao remover forma de pagamento:", error);
      contextApi.showMessage("Erro ao remover forma de pagamento", true, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: { xs: 1, sm: 2 } }}>
        <Grid container spacing={2}>
          {/* Linha 1 - Cabeçalho e Descrição */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, bgcolor: '#f8f9fa' }}>
              <h4 style={{ margin: '0 0 0 0', color: colors.bluePrimary, textAlign: 'left' }}>
                💳 Formas de Pagamento
              </h4>
              <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                Gestão de formas de pagamento e configurações de parcelas
              </Typography>
            </Paper>
          </Grid>

          {/* Linha 2 - Tabs de Navegação */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, mb: 1 }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange} 
                sx={{ 
                  mb: 2,
                  '& .MuiTab-root': {
                    color: '#666',
                    fontWeight: 'bold',
                    '&.Mui-selected': {
                      color: colors.bluePrimary
                    }
                  }
                }}
              >
                <Tab label="Formas de Pagamento" />
                <Tab label="Parcelas" />
              </Tabs>
            </Paper>
          </Grid>

          {activeTab === 0 ? (
            // Formas de Pagamento Tab
            <>
              {/* Linha 3 - Formulário de Forma de Pagamento */}
              <Grid item xs={12}>
                <Paper sx={{ p: 1, mb: 1 }}>
                  <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>
                    🆕 {editingFormaPagamento ? "Editar Forma de Pagamento" : "Nova Forma de Pagamento"}
                  </h4>
                  
                  <Paper sx={{ p: 2, height: "100%", boxShadow: 1, border: '1px solid #e0e0e0' }}>
                    <Box component="form" onSubmit={handleFormaPagamentoSubmit}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            required
                            fullWidth
                            label="Descrição"
                            size="small"
                            value={formaPagamentoData.descricao}
                            disabled={editingFormaPagamento != null}
                            onChange={(e) => handleFormaPagamentoInputChange("descricao", e.target.value)}
                            placeholder="Ex: Dinheiro, Cartão de Crédito, etc."
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            required
                            fullWidth
                            label="Taxa (%)"
                            size="small"
                            value={formaPagamentoData.taxa.toFixed(2).replace('.', ',')}
                            onChange={(e) => handleFormaPagamentoInputChange("taxa", e.target.value)}
                            inputProps={{ inputMode: 'decimal' }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            sx={{ height: '40px' }}
                            disabled={loading}
                          >
                            {loading ? (
                              <DefaultLoader
                                height="1.5em"
                                width="1.5em"
                                colors={["#fff", "#fff", "#fff", "#fff", "#fff"]}
                                textInfo=""
                              />
                            ) : (
                              editingFormaPagamento ? "Atualizar" : "Adicionar"
                            )}
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Paper>
              </Grid>

              {/* Linha 4 - Lista de Formas de Pagamento */}
              <Grid item xs={12}>
                <Paper sx={{ p: 1, mb: 1 }}>
                  <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>📋 Lista de Formas de Pagamento</h4>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, height: "100%", boxShadow: 1, border: '1px solid #e0e0e0' }}>
                        <TableContainer sx={{ overflowX: "auto" }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Descrição</TableCell>
                                <TableCell>Taxa (%)</TableCell>
                                <TableCell align="right">Ações</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {formasPagamento.length > 0 && formasPagamento.slice(0, Math.ceil(formasPagamento.length / 2)).map((forma) => (
                                <TableRow key={forma.id}>
                                  <TableCell>{forma.descricao}</TableCell>
                                  <TableCell>{forma.taxa.toFixed(2)}%</TableCell>
                                  <TableCell align="right">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditFormaPagamento(forma)}
                                      color="primary"
                                      disabled={loading}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => forma.id && handleDeleteFormaPagamento(forma.id)}
                                      color="error"
                                      disabled={loading}
                                    >
                                      <DeleteOutlineIcon />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, height: "100%", boxShadow: 1, border: '1px solid #e0e0e0' }}>
                        <TableContainer sx={{ overflowX: "auto" }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Descrição</TableCell>
                                <TableCell>Taxa (%)</TableCell>
                                <TableCell align="right">Ações</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {formasPagamento.length > 0 && formasPagamento.slice(Math.ceil(formasPagamento.length / 2)).map((forma) => (
                                <TableRow key={forma.id}>
                                  <TableCell>{forma.descricao}</TableCell>
                                  <TableCell>{forma.taxa.toFixed(2)}%</TableCell>
                                  <TableCell align="right">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditFormaPagamento(forma)}
                                      color="primary"
                                      disabled={loading}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => forma.id && handleDeleteFormaPagamento(forma.id)}
                                      color="error"
                                      disabled={loading}
                                    >
                                      <DeleteOutlineIcon />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </>
          ) : (
            // Parcelas Tab
            <>
              {/* Linha 3 - Formulário de Parcela */}
              <Grid item xs={12}>
                <Paper sx={{ p: 1, mb: 1 }}>
                  <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>
                    🆕 {editingParcela ? "Editar Parcela" : "Nova Parcela"}
                  </h4>
                  
                  <Paper sx={{ p: 2, height: "100%", boxShadow: 1, border: '1px solid #e0e0e0' }}>
                    <Box component="form" onSubmit={handleSubmit}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            required
                            fullWidth
                            label="Label"
                            size="small"
                            value={formData.label}
                            onChange={(e) => handleInputChange("label", e.target.value)}
                            placeholder="Ex: 1x, 2x, etc."
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            required
                            fullWidth
                            label="Número de Parcelas"
                            size="small"
                            type="number"
                            value={formData.quantidadeParcelas}
                            onChange={(e) => handleInputChange("quantidadeParcelas", parseInt(e.target.value))}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            required
                            fullWidth
                            label="Taxa (%)"
                            size="small"
                            value={formData.taxa.toFixed(2).replace('.', ',')}
                            onChange={(e) => handleInputChange("taxa", e.target.value)}
                            inputProps={{ inputMode: 'decimal' }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            sx={{ height: '40px' }}
                            disabled={loadingParcelas}
                          >
                            {loadingParcelas ? (
                              <DefaultLoader
                                height="1.5em"
                                width="1.5em"
                                colors={["#fff", "#fff", "#fff", "#fff", "#fff"]}
                                textInfo=""
                              />
                            ) : (
                              editingParcela ? "Atualizar" : "Adicionar"
                            )}
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Paper>
              </Grid>

              {/* Linha 4 - Lista de Parcelas */}
              <Grid item xs={12}>
                <Paper sx={{ p: 1, mb: 1 }}>
                  <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>📋 Lista de Parcelas</h4>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, height: "100%", boxShadow: 1, border: '1px solid #e0e0e0' }}>
                        <TableContainer sx={{ overflowX: "auto" }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Label</TableCell>
                                <TableCell>Número de Parcelas</TableCell>
                                <TableCell>Taxa (%)</TableCell>
                                <TableCell align="right">Ações</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {parcelas.length > 0 && parcelas.slice(0, Math.ceil(parcelas.length / 2)).map((parcela) => (
                                <TableRow key={parcela.id}>
                                  <TableCell>{parcela.label}</TableCell>
                                  <TableCell>{parcela.quantidadeParcelas}</TableCell>
                                  <TableCell>{parcela.taxa.toFixed(2)}%</TableCell>
                                  <TableCell align="right">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEdit(parcela)}
                                      color="primary"
                                      disabled={loadingParcelas}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => parcela.id && handleDelete(parcela.id)}
                                      color="error"
                                      disabled={loadingParcelas}
                                    >
                                      <DeleteOutlineIcon />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, height: "100%", boxShadow: 1, border: '1px solid #e0e0e0' }}>
                        <TableContainer sx={{ overflowX: "auto" }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Label</TableCell>
                                <TableCell>Número de Parcelas</TableCell>
                                <TableCell>Taxa (%)</TableCell>
                                <TableCell align="right">Ações</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {parcelas.length > 0 && parcelas.slice(Math.ceil(parcelas.length / 2)).map((parcela) => (
                                <TableRow key={parcela.id}>
                                  <TableCell>{parcela.label}</TableCell>
                                  <TableCell>{parcela.quantidadeParcelas}</TableCell>
                                  <TableCell>{parcela.taxa.toFixed(2)}%</TableCell>
                                  <TableCell align="right">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEdit(parcela)}
                                      color="primary"
                                      disabled={loadingParcelas}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => parcela.id && handleDelete(parcela.id)}
                                      color="error"
                                      disabled={loadingParcelas}
                                    >
                                      <DeleteOutlineIcon />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>
    </Box>
  );
}
